import { Component, OnInit, Input, Injector, OnChanges, SimpleChanges, EventEmitter, Output } from '@angular/core';
import { RsswaService } from '../../rsswa.service';
import { RSSWAResource } from '../../rsswa';
import { FormGroup } from '@angular/forms';

import { BaseComponent } from 'src/app/common/base/base.component';
import { FormStatusEnum } from 'src/app/common/enumerations/enumerations';
import { OLogService } from 'src/app/components/olog/olog.service';

import { MessagePlaceholder } from 'src/app/common/models/placeholder';
import { WfTaskLocalResource, WfTaskResource, WfSignature, WfSignatureResource, WFTaskSignatureResource, SaveTaskResource } from 'src/app/components/workflow/workflow';
import { WorkFlowService } from 'src/app/components/workflow/workflow.service';

@Component({
  selector: 'rsswa-section6',
  templateUrl: './rsswa-section6.component.html',
  styleUrls: ['./rsswa-section6.component.scss']
})
/**
 * Component responsible for managing Section 6 of the RSSWA form, including workflow signatures,
 * approvals, and form status transitions.
 *
 * This component handles the display and interaction logic for workflow tasks and signatures
 * specific to Section 6 of the RSSWA process. It manages the state of approvals, enables or disables
 * actions based on the form's status, and provides methods for saving task responses, approving,
 * unapproving, and closing the form. The component also emits loading events during asynchronous
 * operations and updates the UI accordingly.
 *
 * @extends BaseComponent
 * @implements OnInit, OnChanges
 *
 * @input rsswa - The RSSWA resource containing workflow and form data.
 * @output loading - Emits a boolean indicating the loading state during async operations.
 *
 * @remarks
 * - Relies on injected services for workflow, logging, and RSSWA data management.
 * - Updates internal state based on changes to the input RSSWA resource.
 * - Handles workflow signature logic, including required approvals and disabled states.
 * - Provides methods for interacting with the workflow, such as approve, unapprove, close, and saveTask.
 *
 * @example
 * <app-rsswa-section6 [rsswa]="rsswaResource" (loading)="onLoading($event)"></app-rsswa-section6>
 */
export class RsswaSection6Component extends BaseComponent implements OnInit, OnChanges {

  @Input() rsswa?: RSSWAResource | null;
  @Output() loading = new EventEmitter<boolean>();

  public disabled!: boolean;
  public questionDisabled!: boolean;

  // Workflow
  workFlow!: WfTaskLocalResource[];
  workFlowPending!: WfTaskResource[];
  signatures!: WfSignature[];
  wfSignatures!: WfSignatureResource[];
  wfTaskSignature: WFTaskSignatureResource = new WFTaskSignatureResource();
  wfSignCount!: number;

  wfFinish?: WfSignature;
  approvals?: number;
  reqApprovals!: boolean;

  // Form
  public form!: FormGroup;

  constructor(
    private _rsswa: RsswaService,
    public _wf: WorkFlowService,
    private ologService: OLogService,
    protected override injector: Injector
  ) {
    super(injector);
  }

  ngOnInit(): void {
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.refresh();
  }

  /**
   * Refreshes the workflow signatures and related approval data for section 6 of the RSSWA form.
   *
   * This method resets the workflow signature count, retrieves the relevant workflow signatures
   * from the RSSWA data model, and updates the component's internal state, including the list of
   * signatures, approval counts, and disabled states. It also determines if any required approvals
   * are pending and prepares the finish signature if the workflow task is marked as 'Close'.
   *
   * Side effects:
   * - Updates `wfSignCount`, `wfSignatures`, `signatures`, `approvals`, `reqApprovals`, `wfFinish`, and `disabled` properties.
   * - Calls `setDisabled()` to update the disabled state.
   *
   * Preconditions:
   * - Assumes `this.rsswa` and its nested properties are properly initialized.
   *
   * @returns {void}
   */
  public refresh() {
    this.wfSignCount = 0;
    if (this.rsswa?.wfTable) {
      this.wfSignatures = this.rsswa.wfTable.wfTableLocal?.wfSectionLocals.find(s => s.number == 6)?.wfSignatures ?? [];
      this.signatures = [];
      this.approvals = this.wfSignatures.length;
      this.reqApprovals = this.wfSignatures.some(t => t.value && t.approveEnabled && !t.wfTaskLocal.wfActionLocals?.some(x => x.name == 'Close'));
      this.setDisabled();
      this.wfSignatures.map(t => {
        const taskEnabled = (t.approveEnabled || t.unapproveEnabled) && this.rsswa?.status != FormStatusEnum.Canceled && this.rsswa?.status != FormStatusEnum.Closed;
        if (!t.wfTaskLocal.wfActionLocals?.some(x => x.name == 'Close')) {
          let signature: WfSignature = {
            id: t.id,
            approveActionId: t.approveID,
            unapproveActionId: t.unapproveID,
            disapproveActionId: t.disapproveID,
            amendmentId: t.amendmentID,
            name: t.titleBefore,
            question: t.question,
            roles: t.roles.map(r => r.id),
            roleCodes: t.roles.map(r => r.code).join('/'),
            disabled: this.disabled || !taskEnabled,
            approvedBy: t.signedBy,
            approved: t.signed,
            approvedOn: t.signedOn,
            approveAction: t.approveEnabled && t.value,
            unapproveAction: t.unapproveEnabled && t.value,
            disapproveAction: t.disapproveEnabled,
            amendment: t.amendmentEnabled,
            required: t.required,
            taskId: t.taskID,
            value: t.value
          };
          this.wfSignCount = this.signCount();
          this.signatures.push(signature);
        }
        else {
          this.wfFinish = {
            id: t.id,
            approveActionId: t.approveID,
            name: t.titleBefore,
            roles: t.roles.map(r => r.id),
            roleCodes: t.roles.map(r => r.code).join('/'),
            disabled: this.disabled || this.reqApprovals,
            approvedBy: t.signedBy,
            approved: t.signed,
            approvedOn: t.signedOn,
            required: t.required,
            question: t.question,
            value: t.value,
            taskId: t.taskID,
            approveAction: true,
            unapproveAction: false,
            amendment: false
          };
        }
      });
    }
  }

  setDisabled() {
    this.disabled = this.rsswa?.status != FormStatusEnum.WorkCompleted;
    this.questionDisabled = this.rsswa?.status == FormStatusEnum.Closed || this.rsswa?.status == FormStatusEnum.Canceled || !this.getCurrentUser();
  }


  answered(taskId: any, e: any) {
    let saveTask: SaveTaskResource = {
      id: taskId,
      value: e
    };

    this.saveTask(saveTask);
  }

  saveTask(saveTask: SaveTaskResource) {
    this.loading.emit(true);
    this._wf.save(saveTask).subscribe(() => {
    }, error => {
      console.log(error);
      this.alert.defaultError();
    });
  }

  /**
   * Approves a workflow task by setting the task and action IDs, emitting a loading event,
   * and invoking the sign operation on the workflow service.
   *
   * @param taskId - The ID of the task to approve (optional).
   * @param actionId - The ID of the action to perform (optional).
   *
   * Emits a loading event and handles errors by logging and displaying a default error alert.
   */
  approve(taskId?: number, actionId?: number) {
    this.wfTaskSignature.taskId = taskId;
    this.wfTaskSignature.actionId = actionId;
    this.loading.emit(true);
    this._wf.sign(this.wfTaskSignature).subscribe(() => {
    }, error => {
      console.log(error);
      this.alert.defaultError();
    });
  }

  // async close(taskId?: number, actionId?: number) {
  //   if (!this.rsswa?.id) return;
  //   const response = await this.ologService.postToOlog(FormType.RSSWA, this.rsswa?.id, OlogNotificationType.Closed);
  //   if (response?.ok) {
  //     this.wfTaskSignature.taskId = taskId;
  //     this.wfTaskSignature.actionId = actionId;
  //     this.loading.emit(true);
  //     this._wf.sign(this.wfTaskSignature).subscribe(() => {
  //       if (this.rsswa) {
  //         const status: SaveRSSWAStatusResource = {
  //           id: this.rsswa.id,
  //           status: FormStatusEnum.Closed
  //         };
  //         this._rsswa.putStatus(status).subscribe(
  //           () => {
  //             this.alert.message('formStateChanged', [new MessagePlaceholder('{serialNo}', this.rsswa?.serialNo), new MessagePlaceholder('{status}', 'Closed')]);
  //           },
  //           error => {
  //             console.error(error);
  //             this.alert.defaultError();
  //           }
  //         );
  //       }
  //     }, error => {
  //       console.log(error);
  //       this.alert.defaultError();
  //     });
  //   }
  // }


  /**
   * Closes the current RSSWA form by submitting the task signature and updating the form status.
   *
   * @param taskId - The ID of the workflow task to close.
   * @param actionId - The ID of the action to perform on the task.
   *
   * Emits a loading event while the operation is in progress.
   * On success, updates the local RSSWA form data and displays a status change message if the form is closed.
   * On failure, logs the error and displays a default error alert.
   */
  close(taskId: number, actionId: number) {
    if (this.rsswa) {
      this.wfTaskSignature.taskId = taskId;
      this.wfTaskSignature.actionId = actionId;
      this.loading.emit(true);
      this._rsswa.closeForm(this.rsswa.id, this.wfTaskSignature).toPromise().then(data => {
        if (data) {
          this.rsswa = data;
          if (this.rsswa.status == FormStatusEnum.Closed)
            this.alert.message('formStateChanged', [new MessagePlaceholder('{serialNo}', this.rsswa?.serialNo), new MessagePlaceholder('{status}', 'Closed')]);
        }
      }).catch(error => {
        console.error(error);
        this.alert.defaultError();
      })
        .finally(() => this.loading.emit(false));
    }
  }

  unapprove(taskId?: number, actionId?: number) {
    this.wfTaskSignature.taskId = taskId;
    this.wfTaskSignature.actionId = actionId;
    this.loading.emit(true);
    this._wf.sign(this.wfTaskSignature).subscribe(() => {
      this.loading.emit(false);
    }, error => {
      console.log(error);
      this.alert.defaultError();
    });
  }

  signCount() {
    return this.wfSignatures.filter(t => !t.signed).length - 1;
  }
}
